/**
 * Brand preview — live mock of the surfaces a clinic's patients see, rendered
 * with the current AdminSettings values (logo, favicon, colors, tagline,
 * clinic name). Helps clinic owners pick colors and copy with confidence
 * before going live.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │ 1. Portal nav header   (sidebar + topbar)    │
 *   │ 2. Patient-facing invoice header (FV-style) │
 *   │ 3. Outbound email signature                  │
 *   │ 4. Booking confirmation card                 │
 *   └─────────────────────────────────────────────┘
 */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { api, formatApiError } from '../../lib/api';
import { BrandLogo } from '../../components/BrandLogo';
import { Eye, Printer, Mail, Calendar, MapPin, Phone, Globe2, FileText, Download, Send, Loader2 } from 'lucide-react';

export default function AdminBrandPreview() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get('/clinic/settings').then(r => setS(r.data)); }, []);

  if (!s) return <div className="p-8 text-fg-muted">Loading…</div>;

  const brand = s.brand_primary_color || '#003D80';
  const accent = s.brand_accent_color || '#FFB000';
  const logo = s.clinic_logo_url || '/logo.jpg';
  const favicon = s.clinic_favicon_url || '/favicon.svg';
  const name = s.clinic_name || 'Physiogenic';
  const tagline = s.clinic_tagline || 'Recover · Rehab · Train · Prevent';
  const address = s.address || 'Warsaw, Poland';
  const phone = s.phone || '';
  const email = s.email || '';
  const website = s.website || '';

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-medium mb-2" style={{ color: accent }}>BRANDING</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Brand preview</h1>
          <p className="text-sm text-fg-muted mt-1">
            What your patients see across the portal, invoices, and email. Edit values in <a className="text-brand underline" href="/admin/settings">Settings</a> and refresh here.
          </p>
        </div>
        <button onClick={() => window.location.reload()} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="bp-refresh">
          <Eye className="w-4 h-4"/> Refresh preview
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">

        {/* 1. Portal nav header preview */}
        <section className="surface border border-soft rounded-2xl overflow-hidden" data-testid="bp-portal">
          <header className="px-4 py-3 border-b border-soft flex items-center justify-between text-xs uppercase tracking-wider text-fg-muted">
            <span>Patient portal · sidebar</span>
            <span className="font-mono">desktop ≥ 768 px</span>
          </header>
          <div className="flex">
            <aside className="w-56 p-4 border-r border-soft" style={{ background: '#fff' }}>
              <div className="flex items-center gap-2 pb-3 border-b border-soft">
                <BrandLogo size={48} src={logo}/>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{name}</div>
                  <div className="text-[10px] text-fg-muted uppercase tracking-wider truncate">{tagline}</div>
                </div>
              </div>
              <nav className="mt-3 space-y-1.5">
                {['Dashboard', 'Book session', 'Appointments', 'My progress', 'Documents'].map((n, i) => (
                  <div key={n}
                       className={`px-3 py-2 rounded-md text-xs ${i === 0 ? 'font-medium' : 'text-fg-muted'}`}
                       style={i === 0 ? { background: hexToBg(brand), color: brand } : {}}>
                    {n}
                  </div>
                ))}
              </nav>
            </aside>
            <div className="flex-1 p-4">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: accent }}>WELCOME BACK</div>
              <h2 className="font-heading font-bold text-xl mt-1">Hi, Anna 👋</h2>
              <button className="mt-4 px-4 py-2 rounded-md text-white text-sm font-medium"
                      style={{ background: brand }} data-testid="bp-cta-primary">
                Book your next session
              </button>
              <button className="mt-4 ml-2 px-4 py-2 rounded-md text-white text-sm font-medium"
                      style={{ background: accent }} data-testid="bp-cta-accent">
                See progress
              </button>
            </div>
          </div>
        </section>

        {/* 2. Invoice header preview */}
        <section className="surface border border-soft rounded-2xl overflow-hidden" data-testid="bp-invoice">
          <header className="px-4 py-3 border-b border-soft flex items-center justify-between text-xs uppercase tracking-wider text-fg-muted">
            <span>Invoice header · FV-style</span>
            <Printer className="w-4 h-4"/>
          </header>
          <div className="p-6 bg-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <BrandLogo size={64} src={logo}/>
                <div>
                  <div className="font-heading font-bold text-lg" style={{ color: brand }}>{name}</div>
                  <div className="text-xs text-fg-muted">{address}</div>
                  <div className="text-xs text-fg-muted">{phone || '+48 22 123 45 67'}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-2xl" style={{ color: brand }}>FV/2026/001</div>
                <div className="text-xs text-fg-muted mt-1">Issued: 14 Feb 2026</div>
                <span className="inline-block mt-2 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded"
                      style={{ background: hexToBg(accent), color: accent }}>
                  PAID
                </span>
              </div>
            </div>
            <hr className="my-4 border-soft"/>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-fg-muted">
                <tr><th className="text-left py-1">Service</th><th className="text-right py-1">Net</th><th className="text-right py-1">Gross</th></tr>
              </thead>
              <tbody>
                <tr><td className="py-1">Initial assessment · 60 min</td><td className="text-right">200 PLN</td><td className="text-right">200 PLN</td></tr>
                <tr><td className="py-1">Manual therapy · 45 min</td><td className="text-right">180 PLN</td><td className="text-right">180 PLN</td></tr>
              </tbody>
              <tfoot className="font-semibold border-t border-soft">
                <tr><td className="pt-2">Total</td><td className="text-right pt-2">380 PLN</td><td className="text-right pt-2" style={{ color: brand }}>380 PLN</td></tr>
              </tfoot>
            </table>
            <div className="text-[10px] text-fg-muted mt-3">VAT-exempt under PL Art. 43 ust. 1 pkt 18 — medical services.</div>
          </div>
        </section>

        {/* 3. Email signature preview */}
        <section className="surface border border-soft rounded-2xl overflow-hidden" data-testid="bp-email">
          <header className="px-4 py-3 border-b border-soft flex items-center justify-between text-xs uppercase tracking-wider text-fg-muted">
            <span>Email signature · outbound</span>
            <Mail className="w-4 h-4"/>
          </header>
          <div className="p-6 bg-white text-sm">
            <p>Dear Anna,</p>
            <p className="mt-3">Thanks for confirming your session. We've reserved 14 Feb 2026 at 14:00. Please arrive 5 minutes early so we can settle you in.</p>
            <p className="mt-3">If you need to reschedule, just reply to this email.</p>
            <p className="mt-4">Warm regards,</p>
            <hr className="my-3 border-soft"/>
            <table className="text-xs">
              <tbody>
                <tr>
                  <td className="pr-3 align-top">
                    <BrandLogo size={56} src={logo}/>
                  </td>
                  <td className="align-top">
                    <div className="font-heading font-bold text-sm" style={{ color: brand }}>{name}</div>
                    <div className="text-fg-muted">{tagline}</div>
                    <div className="mt-2 space-y-0.5">
                      {address && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" style={{ color: accent }}/> {address}</div>}
                      {phone   && <div className="flex items-center gap-1.5"><Phone  className="w-3 h-3" style={{ color: accent }}/> {phone}</div>}
                      {email   && <div className="flex items-center gap-1.5"><Mail   className="w-3 h-3" style={{ color: accent }}/> {email}</div>}
                      {website && <div className="flex items-center gap-1.5"><Globe2 className="w-3 h-3" style={{ color: accent }}/> {website}</div>}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. Booking confirmation card */}
        <section className="surface border border-soft rounded-2xl overflow-hidden" data-testid="bp-booking">
          <header className="px-4 py-3 border-b border-soft flex items-center justify-between text-xs uppercase tracking-wider text-fg-muted">
            <span>Booking confirmation · patient app</span>
            <Calendar className="w-4 h-4"/>
          </header>
          <div className="p-6">
            <div className="rounded-xl overflow-hidden shadow-lg max-w-md mx-auto">
              <div className="p-5 text-white" style={{ background: `linear-gradient(135deg, ${brand}, ${shade(brand, -25)})` }}>
                <div className="flex items-center gap-3">
                  <BrandLogo size={52} src={logo} className="bg-white"/>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: accent }}>CONFIRMED</div>
                    <div className="font-heading font-bold text-lg">{name}</div>
                  </div>
                </div>
                <div className="mt-4 text-3xl font-heading font-bold">Fri · 14 Feb</div>
                <div className="text-sm opacity-90 mt-1">14:00 · 45 min · with Dr. Adamski</div>
              </div>
              <div className="bg-white p-4 text-xs">
                <div className="flex items-center gap-2 text-fg-muted">
                  <MapPin className="w-3 h-3" style={{ color: accent }}/>
                  <span>{address}</span>
                </div>
                <button className="mt-3 w-full py-2 rounded-md text-white text-xs font-medium"
                        style={{ background: accent }} data-testid="bp-add-cal">
                  Add to calendar
                </button>
                <button className="mt-2 w-full py-2 rounded-md border text-xs font-medium"
                        style={{ borderColor: brand, color: brand }} data-testid="bp-reschedule">
                  Reschedule
                </button>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Iter 122 — Live PDF preview. Renders REAL invoices / treatment plans /
          visit notes with fake sample data using the current tenant brand.
          Owners can visually confirm what patients receive before going live. */}
      <PdfPreviewSection accent={accent} brand={brand}/>

      {/* Variables read */}
      <div className="mt-8 surface border border-soft rounded-2xl p-4 text-xs" data-testid="bp-vars">
        <div className="text-fg-muted uppercase tracking-wider mb-3">Variables in use</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Var label="Primary color" value={brand}><span className="w-5 h-5 rounded-full inline-block border border-black/10" style={{ background: brand }}/></Var>
          <Var label="Accent color"  value={accent}><span className="w-5 h-5 rounded-full inline-block border border-black/10" style={{ background: accent }}/></Var>
          <Var label="Clinic name"   value={name}/>
          <Var label="Tagline"       value={tagline}/>
          <Var label="Logo"          value={logo === '/logo.jpg' ? 'default' : 'custom'}>
            <img src={logo} alt="logo" className="w-5 h-5 rounded-sm object-cover border border-soft"/>
          </Var>
          <Var label="Favicon"       value={favicon === '/favicon.svg' ? 'default' : 'custom'}>
            <img src={favicon} alt="favicon" className="w-5 h-5 rounded-sm object-contain border border-soft p-0.5 bg-white"/>
          </Var>
          <Var label="Phone"   value={phone   || '—'}/>
          <Var label="Email"   value={email   || '—'}/>
        </div>
      </div>
    </div>
  );
}

function Var({ label, value, children }) {
  return (
    <div className="flex items-center gap-2">
      {children}
      <div className="min-w-0">
        <div className="text-fg-muted">{label}</div>
        <div className="font-medium truncate" title={value}>{value}</div>
      </div>
    </div>
  );
}


/**
 * Iter 122 — Real PDF preview tabs. Fetches /api/branding/preview/{type}
 * as blob URLs so we can drop them into <iframe> without triggering a
 * download. Cache-busts on tab switch via a monotonic counter so admins
 * always see the fresh branding after they save Settings.
 */
const PDF_TYPES = [
  { key: 'invoice',        label: 'Invoice',        icon: Printer  },
  { key: 'treatment-plan', label: 'Treatment plan', icon: FileText },
  { key: 'visit-note',     label: 'Visit note',     icon: FileText },
];

function PdfPreviewSection({ accent, brand }) {
  const [active, setActive] = useState('invoice');
  const [bust, setBust] = useState(0);
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);

  const previewUrl = useMemo(() => urls[active], [urls, active]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    const load = async () => {
      try {
        // Iter 146 — render page-1 PNG server-side. Browsers inside sandboxed
        // iframes can't run the native PDF plugin, so a PDF-in-iframe shows
        // blank; an <img> works everywhere. PDF stays available via Download.
        const r = await api.get(`/branding/preview/${active}/image`, { responseType: 'blob' });
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(r.data);
        setUrls((u) => {
          if (u[active]) URL.revokeObjectURL(u[active]);
          return { ...u, [active]: blobUrl };
        });
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message || 'Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [active, bust]);

  useEffect(() => () => {
    Object.values(urls).forEach(u => URL.revokeObjectURL(u));
  }, [urls]);

  const sendTestEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/branding/preview/${active}/email-test`);
      if (data.sent) {
        toast.success(`Sent sample ${active.replace('-', ' ')} to ${data.to}`, {
          description: 'Check your inbox to see how patients will receive it.',
        });
      } else {
        toast.warning('Email service not configured', {
          description: 'The PDF rendered fine, but Resend is not wired up on this environment. Configure RESEND_API_KEY to enable outbound.',
        });
      }
    } catch (e) {
      toast.error('Send failed', {
        description: formatApiError(e.response?.data?.detail) || e.message,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-8 surface border border-soft rounded-2xl overflow-hidden" data-testid="bp-pdf-section">
      <header className="px-4 py-3 border-b border-soft flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: accent }}>
            LIVE DOCUMENT PREVIEW
          </div>
          <h2 className="font-heading font-bold text-lg mt-0.5">
            What your patients will actually receive
          </h2>
          <p className="text-xs text-fg-muted mt-1">
            Real PDFs generated with your current logo, clinic name and colour — using dummy sample data.
            Save changes in <a className="text-brand underline" href="/admin/settings">Settings</a> to see them here.
          </p>
        </div>
        <button onClick={() => setBust(b => b + 1)}
                className="btn-secondary inline-flex items-center gap-2 text-sm shrink-0"
                data-testid="bp-pdf-refresh">
          <Eye className="w-4 h-4"/> Re-render
        </button>
      </header>

      <div className="flex flex-wrap gap-2 px-4 pt-3">
        {PDF_TYPES.map(({ key, label, icon: Icon }) => {
          const on = key === active;
          return (
            <button key={key}
                    onClick={() => setActive(key)}
                    data-testid={`bp-pdf-tab-${key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${on ? 'text-white' : 'text-fg-muted hover:text-fg'}`}
                    style={on ? { background: brand } : { background: 'transparent', border: '1px solid var(--border)' }}>
              <Icon className="w-3.5 h-3.5"/> {label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={async () => {
                    try {
                      const r = await api.get(`/branding/preview/${active}`, { responseType: 'blob' });
                      const u = URL.createObjectURL(r.data);
                      const a = document.createElement('a');
                      a.href = u; a.download = `preview-${active}.pdf`;
                      document.body.appendChild(a); a.click();
                      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(u); }, 800);
                    } catch { toast.error('PDF download failed.'); }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-fg-muted hover:text-fg"
                  style={{ border: '1px solid var(--border)' }}
                  data-testid={`bp-pdf-download-${active}`}>
            <Download className="w-3.5 h-3.5"/> Download PDF
          </button>
          <button onClick={sendTestEmail} disabled={sending || loading || !!err}
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-50"
                   style={{ background: brand }}
                   title="Send this sample PDF to your admin email so you can preview it in Gmail / Outlook exactly as your patients will."
                   data-testid={`bp-pdf-email-${active}`}>
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
            {sending ? 'Sending…' : 'Send to my email'}
          </button>
        </div>
      </div>

      <div className="p-4">
        {loading && (
          <div className="h-[560px] flex items-center justify-center text-sm text-fg-muted" data-testid="bp-pdf-loading">
            Rendering PDF…
          </div>
        )}
        {err && !loading && (
          <div className="h-[560px] flex items-center justify-center text-sm text-[#B83A3A]" data-testid="bp-pdf-error">
            {err}
          </div>
        )}
        {!loading && !err && previewUrl && (
          <div className="max-h-[720px] overflow-auto rounded-lg border border-soft bg-white">
            <img src={previewUrl} alt={`${active} preview`}
                 className="w-full"
                 data-testid={`bp-pdf-iframe-${active}`}/>
          </div>
        )}
      </div>
    </section>
  );
}


/** Hex → translucent CSS background for "active" pill tinting. */
function hexToBg(hex) {
  const v = (hex || '#003D80').replace('#', '');
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r},${g},${b},0.12)`;
}

/** Naive light/dark shade — used for gradient end-stop. */
function shade(hex, percent) {
  const v = (hex || '#003D80').replace('#', '');
  const num = parseInt(v, 16);
  let r = (num >> 16) + Math.round((255 * percent) / 100);
  let g = ((num >> 8) & 0xff) + Math.round((255 * percent) / 100);
  let b = (num & 0xff) + Math.round((255 * percent) / 100);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
